/* eslint-disable class-methods-use-this */
import { toast } from 'react-toastify';

import jwtDecode from 'jwt-decode';

import api from '../api';
import { axiosInstance } from '../axios';
import localization from '../localization';

class Auth {
  constructor() {
    this.handleAuthentication();
    this.setAxiosInterceptors();
  }

  isTokenRefreshing = false;

  setAxiosInterceptors() {
    axiosInstance.interceptors.request.use(
      (config) => {
        const headers = { ...config.headers };
        const token = this.getAccessToken();

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        return { ...config, headers };
      },

      (error) => Promise.reject(error),
    );

    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config.url.includes('/iam/tokens')) {
          const isRefreshSuccessful = await this.refreshToken();

          if (isRefreshSuccessful) {
            const originalRequest = error.config;
            originalRequest._retry = true;

            return axiosInstance(originalRequest);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  handleAuthentication() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken && !refreshToken) {
      return;
    }

    if (this.isValidToken()) {
      this.setSession(accessToken, refreshToken);
    } else {
      this.logout();
    }
  }

  loginWithEmailAndPassword(username, password, realm = 'nexway') {
    return api.signIn({ username, password, realm }).then(({ data }) => {
      if (data.access_token && data.twoFA) {
        return data;
      }

      if (data.access_token) {
        this.setSession(data.access_token, data.refresh_token);
        this.setAxiosInterceptors();

        return data;
      }

      return data.error;
    });
  }

  loginWith2FA(token, code, realm = 'nexway') {
    return api.signInTwoFa({ token, code2fa: code, realm }).then(({ data }) => {
      if (data.access_token) {
        this.setSession(data.access_token, data.refresh_token);
        this.setAxiosInterceptors();

        return data;
      }

      return data.error;
    });
  }

  signInWithBackupCode(email, backupCode, realm = 'nexway') {
    return api.signInWithBackupCode({ email, backupCode, realm }).then(({ data }) => {
      if (data.access_token) {
        this.setSession(data.access_token, data.refresh_token);
        this.setAxiosInterceptors();

        return data;
      }

      return data.error;
    });
  }

  async refreshToken() {
    const refresh = this.getRefreshToken();
    const realm = this.getRealm();

    if (!this.isTokenRefreshing) {
      this.isTokenRefreshing = true;
    } else {
      await new Promise((resolve) => {
        const intervalTimer = setInterval(() => {
          if (!this.isTokenRefreshing) {
            clearInterval(intervalTimer);
            resolve();
          }
        }, 300);
      });

      return true;
    }

    try {
      toast(localization.t('general.recoverSession'));

      const { data } = await api.signIn({
        refresh_token: refresh,
        grantType: 'refresh_token',
        realm,
      });

      if (data?.access_token) {
        this.setSession(data.access_token, data.refresh_token);
        this.setAxiosInterceptors();
        this.isTokenRefreshing = false;
        toast(localization.t('general.sessionHasBeenRecovered'));

        return true;
      }
    } catch (error) {
      this.logout();
      window.location.reload();
    }

    return false;
  }

  logout() {
    this.setSession(null);
  }

  setSession(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('decodedToken', JSON.stringify(jwtDecode(accessToken)));
    } else {
      localStorage.removeItem('nexwayState');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('realm');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('decodedToken');
    }
  }

  getAccessToken() {
    return localStorage.getItem('accessToken') === 'null'
      ? null
      : localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken') === 'null'
      ? null
      : localStorage.getItem('refreshToken');
  }

  getRealm() {
    return localStorage.getItem('realm');
  }

  decodeToken(accessToken) {
    return jwtDecode(accessToken);
  }

  isValidToken() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken && !refreshToken) {
      return false;
    }

    const decoded = this.decodeToken(accessToken);
    const currentTime = Date.now() / 1000;

    if (decoded.exp < currentTime) {
      return this.refreshToken();
    }

    return decoded.exp > currentTime;
  }

  isSignedIn() {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  }

  createGoogleSession(realm = 'nexway') {
    return api.createGoogleSession({ realm }).then(({ data }) => {
      return data;
    });
  }

  loginWithGoogle(idToken, realm = 'nexway') {
    return api.connectGoogle({ idToken, realm }).then(({ data }) => {
      if (data.access_token) {
        this.setSession(data.access_token, data.refresh_token);
        this.setAxiosInterceptors();

        return data;
      }

      return data.error;
    });
  }
}

const auth = new Auth();

export default auth;
